/**
 * The failure that has no screen — and how the business finds out about it anyway.
 *
 * ── THE SITUATION THIS FILE IS THE ANSWER TO ─────────────────────────────────
 *
 * `packages/contracts/src/errors.ts` has nine codes because nine states were drawn, and
 * it deliberately has no generic one: the design drew no something-went-wrong screen, and
 * `states/__tests__/states.test.tsx` forbids the generic sentence from existing anywhere
 * in this tree, comments included. So when the Worker answers with a failure this build
 * has no state for, `api/client.ts` throws `UnmappedApiError` and `data/resource.ts`
 * surfaces it separately from the nine, and the screen renders UNCHANGED.
 *
 * That rule is right and this file does not weaken it. What it fixes is the other half:
 * "invisible to the customer" had become "invisible to everybody". A 500 from a route
 * nobody had wired produced a screen with no rows on it, no line in a console, and no
 * trace anywhere — indistinguishable, to a customer AND to the person they phoned, from
 * an account that genuinely has no orders in it. The one condition the contracts file
 * says "is meant to be noticed" was the only one nobody could notice.
 *
 * ── WHAT IS REPORTED, AND THE THREE PLACES IT LANDS ──────────────────────────
 *
 *   1. `console.error`, once per occurrence, with the status, the surface it happened on
 *      and the payload the server actually sent. This is what a developer with the phone
 *      in their hand reads.
 *   2. A bounded in-memory log, `unmappedReports()`. An operator on a support call can
 *      read it back; so can a test, which is why the console is not the only sink.
 *   3. `window.__oprappo_unmapped` — the same log, reachable from a remote debugging
 *      session or a pasted one-liner without the operator needing this module. It is a
 *      GETTER over the same array rather than a second copy, so there is one truth.
 *
 * ── AND THE THREE THINGS IT REFUSES TO DO ────────────────────────────────────
 *
 * NO TENTH CODE. Nothing here mints an `ErrorCode`, maps onto one of the nine, or reaches
 * `ERROR_CODE_STATE`. An unmapped failure stays unmapped; this only writes it down.
 *
 * NO SCREEN, AND NO BANGLA. Rendering something would need a sentence nobody drew, in a
 * language most reviewers of this tree cannot check (CLAUDE.md; docs/01 § prohibited).
 * Every string in this file is English, addressed to an operator, and never rendered.
 *
 * NO NETWORK. There is no error-reporting endpoint in `packages/contracts` and inventing
 * one would be inventing an API. The log is the SLOT: when a `POST /v1/ops/client-errors`
 * exists, it is fed from `reportUnmapped` and nothing else in the app changes. Recorded in
 * `docs/bangla-needed/session-safety.md § what is still open` — it needs no Bangla, but it
 * is the same kind of open dependency and there is one list.
 *
 * ── WHY THE LOG IS BOUNDED ───────────────────────────────────────────────────
 * A screen that fails once tends to fail on every retry. An unbounded array behind a
 * reload loop is a memory leak on a phone with 2 GB in it, so the window is small and the
 * OLDEST entry is dropped: the first occurrence of a fault is the interesting one, but a
 * report nobody can hold is worse than a short one.
 */
import { UnmappedApiError } from '../api/client.js';

/** One recorded occurrence. English only — see the header. */
export interface UnmappedReport {
  /**
   * Which of the two shapes this is.
   *
   * `api`     the server answered with a failure no drawn state covers — an
   *           `UnmappedApiError` out of `api/client.ts`.
   * `undrawn` the app itself reached a condition the design has no frame for and chose
   *           to render nothing rather than invent one. `H2 · ফেরতের শর্ত` asked for a
   *           refund key nobody has written terms for is the first of these: showing
   *           another product's terms would be a promise the business then has to keep,
   *           so the sheet does not open, and this is the only trace it leaves.
   *
   * Both are the same category and belong in the same log: invisible to the customer,
   * and the business needs to know. They are not the same event, so they are labelled.
   */
  kind: 'api' | 'undrawn';
  /** Where it happened, in the app's own vocabulary: a surface, a route, a call. */
  where: string;
  /** The HTTP status the server answered with. `null` on an `undrawn` report. */
  status: number | null;
  /** The body, or — for `undrawn` — a short English note naming what was missing. */
  payload: unknown;
  /** ISO-8601, from the browser's clock. Approximate, and never shown to a customer. */
  at: string;
}

/** How many occurrences are kept. See the header for why this is small. */
const WINDOW = 20;

/** The name the log answers to on `window`. One name, in one place. */
export const UNMAPPED_WINDOW_KEY = '__oprappo_unmapped';

const log: UnmappedReport[] = [];

/**
 * Every unmapped failure this tab has seen, oldest first.
 *
 * A COPY, so a caller reading the log cannot edit it. The array behind
 * `window.__oprappo_unmapped` is this same list through a getter, for the reason in the
 * header: two arrays would eventually disagree, and the one an operator reads would be
 * the stale one.
 */
export function unmappedReports(): readonly UnmappedReport[] {
  return [...log];
}

/** Drop everything recorded. For tests; the app never calls it. */
export function clearUnmappedReports(): void {
  log.length = 0;
}

let exposed = false;

/**
 * Put the log where an operator can reach it without importing anything.
 *
 * `defineProperty` with a getter and no setter: the log can be READ from a console and
 * cannot be replaced from one, so a page that has been told to paste something into its
 * address bar cannot swap a real fault history for an empty one. Wrapped in try/catch
 * because a frozen or proxied `window` is somebody else's decision to make, and failing to
 * expose a diagnostic must never break the app the diagnostic is about.
 */
function expose(): void {
  if (exposed) return;
  exposed = true;
  try {
    Object.defineProperty(globalThis, UNMAPPED_WINDOW_KEY, {
      configurable: true,
      get: () => unmappedReports(),
    });
  } catch {
    /* the in-module log above is still readable; only the shortcut is missing */
  }
}

/** Append, oldest out first. The one place the window is enforced. */
function push(report: UnmappedReport): void {
  log.push(report);
  if (log.length > WINDOW) log.shift();
}

/**
 * The app reached a condition the design draws nothing for, and rendered nothing.
 *
 * NOT an error from the server — no status, no envelope, nothing to map. It is the
 * other way a customer ends up looking at a screen that is missing something: the data
 * asked for a frame that was never drawn. Recorded in the same log and for the same
 * reason, because the alternative in both cases is a gap nobody can see from outside.
 *
 * `detail` is ENGLISH and is never rendered. The customer-facing half of this gap is a
 * Bangla sentence nobody has written, and writing one here is what docs/01 forbids; the
 * sentence is asked for in `docs/bangla-needed/session-safety.md` instead.
 *
 * Call it from an effect, not from a render body — a render can happen twice and a log
 * with every gap in it twice is a log whose counts nobody trusts.
 */
export function reportUndrawn(where: string, detail: string): void {
  expose();
  push({ kind: 'undrawn', where, status: null, payload: detail, at: new Date().toISOString() });
  console.error(
    `[oprappo] ${where}: ${detail} Nothing is drawn for this, so the screen renders ` +
      `nothing rather than inventing it. Read the history at window.${UNMAPPED_WINDOW_KEY}; ` +
      `see apps/web/src/states/unmapped.ts.`,
  );
}

/**
 * Write one unmapped failure down. Called at the catch, never from a render.
 *
 * A render can happen twice — React's StrictMode runs it twice on purpose — and a log
 * with every fault in it twice is a log whose counts nobody trusts. `data/resource.ts`
 * calls this in its `.catch`, which happens once per load.
 *
 * Anything that is not an `UnmappedApiError` is ignored rather than coerced: the nine
 * designed failures already reach a drawn screen through `boundary.tsx`, and recording
 * them here would make this log the very generic-failure channel the contracts file
 * exists to prevent.
 */
export function reportUnmapped(where: string, cause: unknown): void {
  if (!(cause instanceof UnmappedApiError)) return;
  expose();

  push({
    kind: 'api',
    where,
    status: cause.status,
    payload: cause.payload,
    at: new Date().toISOString(),
  });

  console.error(
    `[oprappo] ${where}: the server returned a failure with no designed state ` +
      `(HTTP ${String(cause.status)}). Nothing is drawn for this, so the customer sees ` +
      `the screen unchanged. Read the history at window.${UNMAPPED_WINDOW_KEY}; ` +
      `see apps/web/src/states/unmapped.ts.`,
    cause.payload,
  );
}
